import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { db, auth } from '../firebase';
import { DESIGN_TOKENS } from '../constants/designTokens';
import AppButton from '../components/ui/AppButton';
import AppCard from '../components/ui/AppCard';
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    addDoc,
    serverTimestamp,
    updateDoc,
    doc,
    arrayUnion,
    writeBatch
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        message: '',
        resolve: null,
    });

    const [notifications, setNotifications] = useState([]);
    const [organizationId, setOrganizationId] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(auth.currentUser?.uid || null);
    const notificationsRef = useRef([]);
    const toastBurstRef = useRef({ startAt: 0, shown: 0, suppressed: 0, timer: null });
    const brand = DESIGN_TOKENS.brand;

    const MAX_NOTIFICATIONS = 50;

    const pushToast = useCallback((message, type = 'info') => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 4000);
    }, []);

    const showToast = useCallback((message, type = 'info') => {
        const now = Date.now();
        const burst = toastBurstRef.current;
        const BURST_WINDOW_MS = 1800;
        const MAX_VISIBLE_IN_WINDOW = 2;

        if (now - burst.startAt > BURST_WINDOW_MS) {
            if (burst.timer) clearTimeout(burst.timer);
            toastBurstRef.current = { startAt: now, shown: 0, suppressed: 0, timer: null };
        }

        const activeBurst = toastBurstRef.current;
        if (activeBurst.shown < MAX_VISIBLE_IN_WINDOW) {
            activeBurst.shown += 1;
            pushToast(message, type);
            return;
        }

        activeBurst.suppressed += 1;
        if (!activeBurst.timer) {
            activeBurst.timer = setTimeout(() => {
                const latest = toastBurstRef.current;
                if (latest.suppressed > 0) {
                    pushToast(`새 알림 ${latest.suppressed}건이 추가로 도착했어요`, 'info');
                }
                toastBurstRef.current = { startAt: Date.now(), shown: 0, suppressed: 0, timer: null };
            }, BURST_WINDOW_MS);
        }
    }, [pushToast]);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const showConfirm = useCallback((message) => {
        return new Promise((resolve) => {
            setConfirmState({
                isOpen: true,
                message,
                resolve,
            });
        });
    }, []);

    const handleConfirm = () => {
        if (confirmState.resolve) confirmState.resolve(true);
        setConfirmState({ isOpen: false, message: '', resolve: null });
    };

    const handleCancel = () => {
        if (confirmState.resolve) confirmState.resolve(false);
        setConfirmState({ isOpen: false, message: '', resolve: null });
    };

    const initNotifications = useCallback((orgId) => {
        if (process.env.NODE_ENV !== 'production') {
            console.log('[Notification] Init for org:', orgId);
        }
        setOrganizationId(orgId);
    }, []);

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            setCurrentUserId(user?.uid || null);
        });
        return () => unsubscribeAuth();
    }, []);

    useEffect(() => {
        return () => {
            if (toastBurstRef.current.timer) {
                clearTimeout(toastBurstRef.current.timer);
            }
        };
    }, []);

    useEffect(() => {
        notificationsRef.current = notifications;
    }, [notifications]);

    useEffect(() => {
        if (!organizationId || !currentUserId) {
            setNotifications([]);
            return;
        }

        const q = query(
            collection(db, 'notifications'),
            where('organizationId', '==', organizationId),
            orderBy('createdAt', 'desc'),
            limit(MAX_NOTIFICATIONS)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const notifs = snapshot.docs.map((notificationDoc) => {
                const data = notificationDoc.data();
                return {
                    id: notificationDoc.id,
                    ...data,
                    date: data.createdAt?.toDate
                        ? data.createdAt.toDate().toISOString()
                        : new Date().toISOString(),
                };
            });
            setNotifications(notifs);
        }, (error) => {
            console.error('Notification Listener Error:', error);
        });

        return () => unsubscribe();
    }, [organizationId, currentUserId]);

    const addNotification = useCallback(async (
        title,
        message,
        type = 'info',
        category = 'system',
        targetId = null,
        overrideOrgId = null,
        targetMenu = null,
        notificationVersion = null
    ) => {
        showToast(message, type);

        const finalOrgId = overrideOrgId || organizationId;
        if (!finalOrgId || !currentUserId) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('[Notification] Missing org/user for save', { finalOrgId, currentUserId });
            }
            return;
        }

        const tempId = `temp_${Date.now()}`;
        const optimisticNotification = {
            id: tempId,
            title,
            message,
            type,
            category,
            targetId,
            targetMenu,
            notificationVersion,
            organizationId: finalOrgId,
            createdBy: currentUserId,
            date: new Date().toISOString(),
            readBy: []
        };
        setNotifications(prev => [optimisticNotification, ...prev]);

        try {
            await addDoc(collection(db, 'notifications'), {
                title,
                message,
                type,
                category,
                targetId,
                targetMenu,
                notificationVersion,
                organizationId: finalOrgId,
                createdBy: currentUserId,
                createdAt: serverTimestamp(),
                readBy: []
            });

            if (notificationsRef.current.length >= MAX_NOTIFICATIONS) {
                const { deleteDoc } = await import('firebase/firestore');
                const toDelete = notificationsRef.current.slice(MAX_NOTIFICATIONS - 1);

                toDelete.forEach((oldNotif) => {
                    if (oldNotif.id && !oldNotif.id.startsWith('temp_')) {
                        deleteDoc(doc(db, 'notifications', oldNotif.id)).catch((err) => {
                            if (process.env.NODE_ENV !== 'production') {
                                console.warn('[Notification] Delete old failed:', err);
                            }
                        });
                    }
                });
            }
        } catch (error) {
            console.error('Failed to save notification to Firestore:', error);
            setNotifications(prev => prev.filter(n => n.id !== tempId));
        }
    }, [organizationId, showToast, currentUserId]);

    const markAsRead = useCallback(async (id) => {
        if (!currentUserId) return;

        try {
            const notifRef = doc(db, 'notifications', id);
            await updateDoc(notifRef, {
                readBy: arrayUnion(currentUserId)
            });
        } catch (error) {
            console.error('Failed to mark as read:', error);
        }
    }, [currentUserId]);

    const markAllAsRead = useCallback(async () => {
        if (!currentUserId || notifications.length === 0) return;

        const unread = notifications.filter(n => !n.readBy?.includes(currentUserId));
        if (unread.length === 0) return;

        try {
            const batch = writeBatch(db);
            unread.forEach((notificationItem) => {
                batch.update(doc(db, 'notifications', notificationItem.id), {
                    readBy: arrayUnion(currentUserId)
                });
            });
            await batch.commit();
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    }, [currentUserId, notifications]);

    const clearAllNotifications = useCallback(async () => {
        await markAllAsRead();
    }, [markAllAsRead]);

    const unreadCount = useMemo(() => {
        if (!currentUserId) return 0;
        return notifications.filter(n => !n.readBy?.includes(currentUserId)).length;
    }, [notifications, currentUserId]);

    const uiNotifications = useMemo(() => {
        if (!currentUserId) return [];

        return notifications
            .filter((n) => {
                if (!n.visibleTo || n.visibleTo.length === 0) return true;
                return n.visibleTo.includes(currentUserId);
            })
            .map((n) => ({
                ...n,
                read: n.readBy?.includes(currentUserId) || false
            }));
    }, [notifications, currentUserId]);

    return (
        <NotificationContext.Provider value={{
            showToast,
            removeToast,
            showConfirm,
            notifications: uiNotifications,
            addNotification,
            markAsRead,
            markAllAsRead,
            clearAllNotifications,
            unreadCount,
            initNotifications
        }}>
            {children}

            {createPortal(
                <div className="fixed top-[calc(env(safe-area-inset-top,0px)+16px)] left-1/2 -translate-x-1/2 z-[2000000] flex flex-col gap-3 w-full max-w-sm px-6 pointer-events-none">
                    {toasts.map(toast => (
                        <div
                            key={toast.id}
                            onClick={() => removeToast(toast.id)}
                            className={`pointer-events-auto cursor-pointer flex items-center gap-3 p-4 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.2)] backdrop-blur-2xl border-2 animate-fade-in-down transition-all hover:scale-[1.02] active:scale-[0.98] ${toast.type === 'success' ? 'bg-emerald-50/95 border-emerald-200 text-emerald-900' :
                                toast.type === 'error' ? 'bg-rose-50/95 border-rose-200 text-rose-900' :
                                    toast.type === 'warning' ? 'bg-amber-50/95 border-amber-200 text-amber-900' :
                                        'bg-white/95 border-gray-200 text-gray-900'
                                }`}
                        >
                            <div className={`shrink-0 p-2 rounded-2xl ${toast.type === 'success' ? 'bg-emerald-100 text-emerald-600' :
                                toast.type === 'error' ? 'bg-rose-100 text-rose-600' :
                                    toast.type === 'warning' ? 'bg-amber-100 text-amber-600' :
                                        'bg-gray-100 text-gray-500'
                                }`}>
                                {toast.type === 'success' ? <Check size={18} strokeWidth={3} /> :
                                    toast.type === 'error' ? <AlertCircle size={18} strokeWidth={3} /> :
                                        toast.type === 'warning' ? <AlertTriangle size={18} strokeWidth={3} /> :
                                            <Info size={18} strokeWidth={3} />}
                            </div>
                            <span className="text-sm font-black flex-1 leading-tight">{toast.message}</span>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeToast(toast.id);
                                }}
                                className="p-2 -mr-1 text-gray-400 hover:text-gray-900 transition-colors rounded-full hover:bg-black/5"
                            >
                                <X size={18} strokeWidth={2.5} />
                            </button>
                        </div>
                    ))}
                </div>,
                document.body
            )}

            {confirmState.isOpen && createPortal(
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fade-in">
                    <AppCard variant="soft" className="rounded-[2rem] p-6 w-full max-w-xs animate-scale-up">
                        <div className="flex flex-col items-center text-center mb-6">
                            <div className={`w-12 h-12 ${brand.primaryLightBgClass} rounded-full flex items-center justify-center mb-3 ${brand.primaryTextClass}`}>
                                <AlertCircle size={24} />
                            </div>
                            <p className={`text-lg font-black ${brand.primaryTextDarkClass} break-keep leading-snug`}>{confirmState.message}</p>
                        </div>
                        <div className="flex gap-2">
                            <AppButton onClick={handleCancel} variant="ghost" className="flex-1">취소</AppButton>
                            <AppButton onClick={handleConfirm} variant="primary" className="flex-1">확인</AppButton>
                        </div>
                    </AppCard>
                </div>,
                document.body
            )}
        </NotificationContext.Provider>
    );
};
